// Pure maths for turning device orientation into a steering-wheel angle.
//
// Device frame (W3C DeviceOrientation spec): x right, y up (in the device's
// natural portrait orientation), z out of the screen towards the viewer.

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

const DEG = Math.PI / 180;

/**
 * Unit vector pointing "down" (towards the ground) in the device frame,
 * from DeviceOrientationEvent beta/gamma in degrees.
 *
 * Derived from R = Rz(alpha) Rx(beta) Ry(gamma): down = R^T (0, 0, -1).
 * The result depends only on the physical orientation, so it stays continuous
 * where the Euler angles themselves jump (e.g. gamma wrapping at +/-90).
 */
export function downVectorFromEuler(betaDeg: number, gammaDeg: number): Vec3 {
  const b = betaDeg * DEG;
  const g = gammaDeg * DEG;
  return {
    x: Math.cos(b) * Math.sin(g),
    y: -Math.sin(b),
    z: -Math.cos(b) * Math.cos(g),
  };
}

/**
 * Express a device-frame vector in screen coordinates (right, up), given the
 * screen orientation angle in degrees (screen.orientation.angle or the legacy
 * window.orientation: 0 portrait, 90 = device top pointing left, 270/-90 =
 * device top pointing right).
 */
export function toScreenFrame(v: Vec3, screenAngleDeg: number): { right: number; up: number } {
  const a = screenAngleDeg * DEG;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return {
    right: v.x * c - v.y * s,
    up: v.x * s + v.y * c,
  };
}

export interface RollReading {
  /** Rotation of the phone about the screen normal, radians. Positive = turned clockwise (steer right). */
  roll: number;
  /** 0..1: how much of gravity lies in the screen plane. Near 0 the phone is flat and roll is unreliable. */
  confidence: number;
}

/** Steering-wheel style roll of the phone, from beta/gamma and screen angle. */
export function rollFromOrientation(betaDeg: number, gammaDeg: number, screenAngleDeg: number): RollReading {
  const down = downVectorFromEuler(betaDeg, gammaDeg);
  const { right, up } = toScreenFrame(down, screenAngleDeg);
  // Held level, "down" points to the bottom of the screen (up = -1). Turning
  // the phone clockwise swings "down" towards the screen's right edge.
  return {
    roll: Math.atan2(right, -up),
    confidence: Math.hypot(right, up),
  };
}

/** Wrap an angle to (-PI, PI]. */
export function wrapAngle(a: number): number {
  let r = a % (2 * Math.PI);
  if (r <= -Math.PI) r += 2 * Math.PI;
  if (r > Math.PI) r -= 2 * Math.PI;
  return r;
}
